import { Close } from '@mui/icons-material';
import { Box, Dialog, Divider, Drawer, IconButton, List, ListSubheader, Toolbar, useTheme } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { AppState } from '@store/store';
import { Palette, Settings } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { useCompanyLogoUrl } from '../../../Helper/logoHelper';
import ThemeEditor from '../../_UnderConstruction/ThemeEditor/ThemeEditor';
import { MobileSearchBox } from '../../General/SearchBox/SearchBox';
import { NotificationButton } from '../Header/NotificationButton';
import { useNavigation } from './NavigationContext';
import { NavigationItem } from './NavigationItem';

interface MobileSidebarProps {
    open: boolean;
    onClose: () => void;
    loggedIn: boolean;
}

/**
 * Mobile Sidebar component for small screens.
 *
 * @param {MobileSidebarProps} props - The props for the mobile sidebar component.
 * @returns {JSX.Element} - The rendered mobile sidebar component.
 */
function MobileSidebar(props: MobileSidebarProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const companyName = useSelector((state: AppState) => state.company.name);
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);
    const permissions = useSelector((state: AppState) => state.permissions);
    const [themeDialogOpen, setThemeDialogOpen] = useState(false);

    const { navItems } = useNavigation();

    if (!props.loggedIn) return null;

    const handleItemClick = () => {
        props.onClose();
    };

    const mainItems = navItems.filter((item) => item.group === 'main');
    const salesItems = navItems.filter((item) => item.group === 'sales');

    return (
        <Drawer
            variant="temporary"
            anchor="right"
            open={props.open}
            onClose={props.onClose}
            ModalProps={{ keepMounted: true }}
            sx={{
                '& .MuiDrawer-paper': {
                    width: 280,
                    boxSizing: 'border-box',
                    height: '100%',
                    borderRadius: 0,
                },
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between', px: 2, position: 'relative' }}>
                <Link to={`/${ELinks.DASHBOARD}`} style={{ textDecoration: 'none' }}>
                    <Box
                        component="img"
                        src={useCompanyLogoUrl(100)}
                        alt={`Logo ${companyName}`}
                        sx={{
                            height: 44,
                            maxWidth: 160,
                            ml: 0,
                            mt: 1,
                            cursor: 'pointer',
                            objectFit: 'contain',
                            p: 1,
                        }}
                    />
                </Link>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: 170,
                        backgroundColor: theme.palette.warning.main,
                        color: theme.palette.warning.contrastText,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}
                >
                    Beta
                </Box>
                <IconButton onClick={props.onClose}>
                    <Close />
                </IconButton>
            </Toolbar>

            <Divider />

            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <List
                    sx={{
                        p: 1,
                        flexGrow: 1,
                        '& .MuiListItemButton-root': {
                            borderRadius: 1,
                            mb: 0.5,
                        },
                    }}
                >
                    {mainItems.map((item) => (
                        <NavigationItem
                            key={item.id}
                            onClick={handleItemClick}
                            path={item.path}
                            title={item.title}
                            icon={item.icon}
                            forceActive={item.forceActive}
                            activeColor={item.activeColor}
                        />
                    ))}

                    {salesItems.length > 0 && (
                        <>
                            <Divider sx={{ my: 1, mx: 2 }} />
                            <ListSubheader
                                sx={{
                                    backgroundColor: 'transparent',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: theme.palette.text.secondary,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    px: 2,
                                    py: 1,
                                    lineHeight: 1.5,
                                }}
                            >
                                Vertrieb
                            </ListSubheader>
                            {salesItems.map((item) => (
                                <NavigationItem
                                    key={item.id}
                                    onClick={handleItemClick}
                                    path={item.path}
                                    title={item.title}
                                    icon={item.icon}
                                />
                            ))}
                        </>
                    )}
                </List>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-around', p: 1, alignItems: 'center' }}>
                    <MobileSearchBox />

                    <NotificationButton />

                    <IconButton
                        onClick={() => {
                            props.onClose();
                            navigate(`/${ELinks.SETTINGS}`);
                        }}
                        sx={{
                            color: theme.palette.primary.main,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: `${theme.palette.primary.main}14`,
                                transform: 'scale(1.05)',
                            },
                        }}
                    >
                        <Settings size={20} />
                    </IconButton>

                    <IconButton
                        onClick={() => setThemeDialogOpen(true)}
                        sx={{
                            color: theme.palette.primary.main,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: `${theme.palette.primary.main}14`,
                                transform: 'scale(1.05)',
                            },
                        }}
                    >
                        <Palette size={20} />
                    </IconButton>

                    <Dialog open={themeDialogOpen} onClose={() => setThemeDialogOpen(false)}>
                        <ThemeEditor showSettingsLink={true} />
                    </Dialog>
                </Box>
            </Box>
        </Drawer>
    );
}

export default MobileSidebar;
